import { useState, useRef, useCallback, useMemo } from 'react';
import {
  X,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Download,
  Loader2,
  FileUp,
  XCircle,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { CustomSelect } from '@/components/ui/custom-select';

// ─── Types ───────────────────────────────────────────────────────────

interface ImportTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (
    transactions: TransactionImportRow[]
  ) => Promise<{ success: boolean; imported: number; errors: string[] }>;
  accounts: { id: string; name: string }[];
  categories: { id: string; name: string; type: 'income' | 'expense' }[];
}

export interface TransactionImportRow {
  amount: number;
  description: string | null;
  date: string;
  type: 'income' | 'expense' | 'transfer';
  category_id: string | null;
  account_id: string | null;
}

interface ParsedRow {
  [key: string]: string;
}

type FieldKey = 'amount' | 'description' | 'date' | 'type' | 'category' | 'account' | '__skip__';

interface ValidatedRow extends TransactionImportRow {
  _valid: boolean;
  _errors: string[];
  _rowIndex: number;
  _categoryName?: string;
  _accountName?: string;
}

type Step = 'upload' | 'map' | 'preview' | 'importing' | 'done';

// ─── Helpers ─────────────────────────────────────────────────────────

const COLUMN_PATTERNS: Record<FieldKey, RegExp[]> = {
  amount: [/^amount$/i, /^montant$/i, /^somme$/i, /^value$/i, /^total$/i, /^prix$/i, /^price$/i],
  description: [
    /^description$/i,
    /^desc$/i,
    /^label$/i,
    /^libelle/i,
    /^libellé/i,
    /^note$/i,
    /^memo$/i,
    /^detail/i,
  ],
  date: [/^date$/i, /^transaction.?date/i, /^posted/i, /^created/i],
  type: [/^type$/i, /^kind$/i, /^direction$/i, /^income.?expense/i],
  category: [/^category$/i, /^categ/i, /^cat[ée]gorie/i, /^categor/i],
  account: [/^account$/i, /^compte$/i, /^account.?name/i, /^source$/i],
  __skip__: [],
};

function autoMapColumn(header: string): FieldKey {
  const clean = header.trim().toLowerCase();
  for (const [field, patterns] of Object.entries(COLUMN_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern.test(clean)) return field as FieldKey;
    }
  }
  return '__skip__';
}

function guessType(value: string): 'income' | 'expense' | 'transfer' {
  const v = value.toLowerCase().trim();
  if (
    ['income', 'revenu', 'crédit', 'credit', 'entrée', 'entree', 'salary', 'salaire'].some((w) =>
      v.includes(w)
    )
  )
    return 'income';
  if (['transfer', 'virement', 'transfert'].some((w) => v.includes(w))) return 'transfer';
  return 'expense';
}

function parseDate(value: string): string | null {
  if (!value) return null;
  const clean = value.trim();

  // ISO format: 2024-01-15
  if (/^\d{4}-\d{2}-\d{2}/.test(clean)) return clean.slice(0, 10);

  // DD/MM/YYYY or DD-MM-YYYY
  const dmy = clean.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;

  // MM/DD/YYYY
  const mdy = clean.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (mdy) {
    const month = parseInt(mdy[1]);
    // If first number > 12, it's day-first
    if (month > 12) return `${mdy[3]}-${mdy[2].padStart(2, '0')}-${mdy[1].padStart(2, '0')}`;
    return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  }

  // Try native Date parsing as fallback
  const parsed = new Date(clean);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);

  return null;
}

function parseAmount(value: string): number {
  // Remove currency symbols and spaces, normalize decimal separator
  const cleaned = String(value).replace(/[^0-9.,-]/g, '');
  // Handle European format: 1.234,56 → 1234.56
  if (/^\d{1,3}(\.\d{3})*(,\d+)?$/.test(cleaned)) {
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  }
  return parseFloat(cleaned.replace(',', '.')) || 0;
}

/** Parse a single CSV line respecting quoted fields (RFC 4180) */
function parseCSVLine(line: string, delimiter: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === delimiter) {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
  }
  fields.push(current.trim());
  return fields;
}

function parseCSV(text: string): { headers: string[]; rows: ParsedRow[] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const firstLine = lines[0];
  const delimiters = [',', ';', '\t'];
  const delimiter = delimiters.reduce((best, d) =>
    parseCSVLine(firstLine, d).length > parseCSVLine(firstLine, best).length ? d : best
  );

  const headers = parseCSVLine(lines[0], delimiter).map((h) => h.replace(/^["']|["']$/g, ''));
  const rows: ParsedRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i], delimiter).map((v) => v.replace(/^["']|["']$/g, ''));
    if (values.some((v) => v)) {
      const row: ParsedRow = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });
      rows.push(row);
    }
  }

  return { headers, rows };
}

async function parseExcel(file: File): Promise<{ headers: string[]; rows: ParsedRow[] }> {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const jsonData: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

  if (jsonData.length === 0) return { headers: [], rows: [] };

  const headers = Object.keys(jsonData[0]);
  const rows: ParsedRow[] = jsonData.map((row) => {
    const parsed: ParsedRow = {};
    headers.forEach((h) => {
      parsed[h] = String(row[h] ?? '');
    });
    return parsed;
  });

  return { headers, rows };
}

function fuzzyMatch(input: string, candidates: { id: string; name: string }[]): string | null {
  const normalized = input.toLowerCase().trim();
  if (!normalized) return null;

  // Exact match
  const exact = candidates.find((c) => c.name.toLowerCase() === normalized);
  if (exact) return exact.id;

  // Partial match
  const partial = candidates.find(
    (c) => c.name.toLowerCase().includes(normalized) || normalized.includes(c.name.toLowerCase())
  );
  return partial?.id ?? null;
}

// ─── Component ───────────────────────────────────────────────────────

export function ImportTransactionsModal({
  isOpen,
  onClose,
  onImport,
  accounts,
  categories,
}: ImportTransactionsModalProps) {
  const { language } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<ParsedRow[]>([]);
  const [columnMap, setColumnMap] = useState<Record<string, FieldKey>>({});
  const [importResult, setImportResult] = useState<{ imported: number; errors: string[] } | null>(
    null
  );
  const [importProgress, setImportProgress] = useState(0);

  const reset = () => {
    setStep('upload');
    setIsDragging(false);
    setFileError(null);
    setFileName('');
    setHeaders([]);
    setRawRows([]);
    setColumnMap({});
    setImportResult(null);
    setImportProgress(0);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = useCallback(
    async (file: File) => {
      setFileError(null);
      setFileName(file.name);

      if (file.size > 5 * 1024 * 1024) {
        setFileError(
          language === 'fr' ? 'Fichier trop volumineux (max 5 Mo)' : 'File is too large (max 5MB)'
        );
        return;
      }

      try {
        let result: { headers: string[]; rows: ParsedRow[] };

        if (
          file.name.endsWith('.csv') ||
          file.name.endsWith('.tsv') ||
          file.name.endsWith('.txt')
        ) {
          const text = await file.text();
          result = parseCSV(text);
        } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          result = await parseExcel(file);
        } else {
          setFileError(
            language === 'fr'
              ? 'Format non supporté. Utilisez CSV, TSV ou Excel.'
              : 'Unsupported format. Use CSV, TSV or Excel.'
          );
          return;
        }

        if (result.headers.length === 0 || result.rows.length === 0) {
          setFileError(
            language === 'fr'
              ? 'Aucune donnée trouvée dans le fichier.'
              : 'No data found in the file.'
          );
          return;
        }

        setHeaders(result.headers);
        setRawRows(result.rows);

        const autoMap: Record<string, FieldKey> = {};
        result.headers.forEach((h) => {
          autoMap[h] = autoMapColumn(h);
        });
        setColumnMap(autoMap);
        setStep('map');
      } catch {
        setFileError(
          language === 'fr' ? 'Erreur lors de la lecture du fichier.' : 'Error reading the file.'
        );
      }
    },
    [language]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  // ── Validated rows ──
  const validatedRows = useMemo((): ValidatedRow[] => {
    const amountCol = Object.entries(columnMap).find(([, v]) => v === 'amount')?.[0];
    const descCol = Object.entries(columnMap).find(([, v]) => v === 'description')?.[0];
    const dateCol = Object.entries(columnMap).find(([, v]) => v === 'date')?.[0];
    const typeCol = Object.entries(columnMap).find(([, v]) => v === 'type')?.[0];
    const categoryCol = Object.entries(columnMap).find(([, v]) => v === 'category')?.[0];
    const accountCol = Object.entries(columnMap).find(([, v]) => v === 'account')?.[0];

    const today = new Date().toISOString().slice(0, 10);

    return rawRows.map((row, index) => {
      const errors: string[] = [];

      // Amount (required)
      const rawAmount = amountCol ? row[amountCol] : '';
      const amount = parseAmount(rawAmount);
      if (!rawAmount || amount === 0)
        errors.push(language === 'fr' ? 'Montant requis' : 'Amount required');

      // Description
      const description = descCol ? row[descCol]?.trim() || null : null;

      // Date
      const rawDate = dateCol ? row[dateCol] : '';
      const date = parseDate(rawDate) || today;
      if (dateCol && rawDate && !parseDate(rawDate))
        errors.push(language === 'fr' ? 'Date invalide' : 'Invalid date');

      // Type — guess from value or column, or infer from amount sign
      let type: 'income' | 'expense' | 'transfer';
      if (typeCol && row[typeCol]) {
        type = guessType(row[typeCol]);
      } else {
        type = amount < 0 ? 'expense' : 'income';
      }

      // Category (fuzzy match to user's categories)
      const rawCategory = categoryCol ? row[categoryCol]?.trim() : '';
      const matchedCategoryId = rawCategory
        ? fuzzyMatch(
            rawCategory,
            categories.filter((c) => c.type === type)
          )
        : null;

      // Account (fuzzy match to user's accounts)
      const rawAccount = accountCol ? row[accountCol]?.trim() : '';
      const matchedAccountId = rawAccount
        ? fuzzyMatch(rawAccount, accounts)
        : (accounts[0]?.id ?? null);

      return {
        amount: Math.abs(amount),
        description,
        date,
        type,
        category_id: type === 'transfer' ? null : matchedCategoryId,
        account_id: matchedAccountId,
        _valid: errors.length === 0,
        _errors: errors,
        _rowIndex: index + 1,
        _categoryName: rawCategory || undefined,
        _accountName: rawAccount || undefined,
      };
    });
  }, [rawRows, columnMap, language, categories, accounts]);

  const validCount = validatedRows.filter((r) => r._valid).length;
  const invalidCount = validatedRows.filter((r) => !r._valid).length;

  const handleImport = async () => {
    setStep('importing');
    setImportProgress(0);

    const validRows = validatedRows.filter((r) => r._valid);
    const transactions: TransactionImportRow[] = validRows.map(
      ({ _valid, _errors, _rowIndex, _categoryName, _accountName, ...rest }) => rest
    );

    const progressInterval = setInterval(() => {
      setImportProgress((prev) => Math.min(prev + 8, 90));
    }, 200);

    const result = await onImport(transactions);

    clearInterval(progressInterval);
    setImportProgress(100);
    setImportResult({ imported: result.imported, errors: result.errors });

    setTimeout(() => setStep('done'), 500);
  };

  const downloadTemplate = () => {
    const csv =
      language === 'fr'
        ? 'Date,Description,Montant,Type,Catégorie,Compte\n2024-01-15,Courses Carrefour,45.90,expense,Alimentation,Compte courant\n2024-01-14,Salaire Janvier,2500,income,Salaire,Compte courant'
        : 'Date,Description,Amount,Type,Category,Account\n2024-01-15,Grocery Store,45.90,expense,Food,Checking\n2024-01-14,January Salary,2500,income,Salary,Checking';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clevia-transactions-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  const fieldOptions: { value: FieldKey; label: string }[] = [
    { value: '__skip__', label: language === 'fr' ? '— Ignorer —' : '— Skip —' },
    { value: 'amount', label: language === 'fr' ? 'Montant' : 'Amount' },
    { value: 'description', label: 'Description' },
    { value: 'date', label: 'Date' },
    { value: 'type', label: 'Type' },
    { value: 'category', label: language === 'fr' ? 'Catégorie' : 'Category' },
    { value: 'account', label: language === 'fr' ? 'Compte' : 'Account' },
  ];

  const typeLabel = (type: string) => {
    if (type === 'income') return language === 'fr' ? 'Revenu' : 'Income';
    if (type === 'expense') return language === 'fr' ? 'Dépense' : 'Expense';
    return language === 'fr' ? 'Virement' : 'Transfer';
  };

  const typeColor = (type: string) => {
    if (type === 'income')
      return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30';
    if (type === 'expense') return 'text-[#78909C] bg-[#78909C]/10 dark:bg-[#78909C]/20';
    return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30';
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700/50">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#16c47f]" />
              {language === 'fr' ? 'Importer des transactions' : 'Import Transactions'}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {language === 'fr'
                ? 'Importez vos transactions depuis un fichier CSV ou Excel'
                : 'Import your transactions from a CSV or Excel file'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>
        </div>

        {/* Step Indicator */}
        {step !== 'done' && step !== 'importing' && (
          <div className="flex items-center gap-2 px-6 pt-4">
            {(['upload', 'map', 'preview'] as Step[]).map((s, i) => {
              const stepIndex = ['upload', 'map', 'preview'].indexOf(step);
              const isActive = i === stepIndex;
              const isDone = i < stepIndex;
              return (
                <div key={s} className="flex items-center gap-2 flex-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      isDone
                        ? 'bg-green-500 text-white'
                        : isActive
                          ? 'bg-[#16c47f] text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {isDone ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                  </div>
                  <span
                    className={`text-xs font-medium ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}
                  >
                    {s === 'upload'
                      ? language === 'fr'
                        ? 'Fichier'
                        : 'Upload'
                      : s === 'map'
                        ? language === 'fr'
                          ? 'Colonnes'
                          : 'Map'
                        : language === 'fr'
                          ? 'Aperçu'
                          : 'Preview'}
                  </span>
                  {i < 2 && (
                    <div
                      className={`flex-1 h-px ${isDone ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ─── STEP: Upload ─── */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-300 ${
                  isDragging
                    ? 'border-[#16c47f] bg-[#16c47f]/5 scale-[1.02]'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.tsv,.txt,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) processFile(file);
                  }}
                />
                <div className="w-16 h-16 bg-[#16c47f]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileUp className="w-8 h-8 text-[#16c47f]" />
                </div>
                <p className="text-gray-900 dark:text-white font-semibold mb-1">
                  {language === 'fr'
                    ? 'Glissez-déposez ou cliquez pour sélectionner'
                    : 'Drag & drop or click to select'}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">
                  CSV, TSV, Excel • Max 5 Mo
                </p>
              </div>

              {fileError && (
                <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400 shrink-0" />
                  <p className="text-red-600 dark:text-red-400 text-sm">{fileError}</p>
                </div>
              )}

              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 text-sm text-[#16c47f] hover:text-[#12b070] transition-colors mx-auto"
              >
                <Download className="w-4 h-4" />
                {language === 'fr' ? 'Télécharger le modèle CSV' : 'Download CSV template'}
              </button>
            </div>
          )}

          {/* ─── STEP: Map Columns ─── */}
          {step === 'map' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {language === 'fr'
                      ? 'Associez les colonnes de votre fichier'
                      : 'Map your file columns'}
                  </p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                    📄 {fileName} • {rawRows.length} {language === 'fr' ? 'ligne(s)' : 'row(s)'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#16c47f]" />
                  <span className="text-xs text-[#16c47f]">
                    {language === 'fr' ? 'Auto-détecté' : 'Auto-detected'}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 divide-y divide-gray-100 dark:divide-gray-600">
                {headers.map((header) => (
                  <div key={header} className="flex items-center justify-between p-4 gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                          A
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {header}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                          {rawRows[0]?.[header] || '—'}
                        </p>
                      </div>
                    </div>
                    <CustomSelect
                      value={columnMap[header] || '__skip__'}
                      onChange={(value) =>
                        setColumnMap((prev) => ({ ...prev, [header]: value as FieldKey }))
                      }
                      className="min-w-[140px]"
                      options={fieldOptions.map((opt) => ({
                        value: opt.value,
                        label: opt.label,
                      }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── STEP: Preview ─── */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-900 dark:text-white font-medium">
                  {language === 'fr' ? "Vérifiez avant d'importer" : 'Review before importing'}
                </p>
                <div className="flex gap-3">
                  <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle2 className="w-4 h-4" /> {validCount}
                  </span>
                  {invalidCount > 0 && (
                    <span className="flex items-center gap-1 text-sm text-red-500 dark:text-red-400">
                      <XCircle className="w-4 h-4" /> {invalidCount}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {validatedRows.map((row) => (
                  <div
                    key={row._rowIndex}
                    className={`p-3 rounded-xl border transition-all ${
                      row._valid
                        ? 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-xs text-gray-400 dark:text-gray-500 w-6 text-right shrink-0">
                          #{row._rowIndex}
                        </span>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-md shrink-0 ${typeColor(row.type)}`}
                        >
                          {typeLabel(row.type)}
                        </span>
                        <span className="text-sm text-gray-900 dark:text-white font-medium truncate">
                          {row.description ||
                            (language === 'fr' ? '(sans description)' : '(no description)')}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <span
                          className={`text-sm font-bold ${row.type === 'income' ? 'text-green-600 dark:text-green-400' : row.type === 'expense' ? 'text-[#78909C]' : 'text-blue-600 dark:text-blue-400'}`}
                        >
                          {row.type === 'income' ? '+' : '-'}
                          {row.amount.toFixed(2)}
                        </span>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{row.date}</p>
                      </div>
                    </div>
                    {!row._valid && (
                      <div className="mt-2 flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        <p className="text-xs text-red-500">{row._errors.join(', ')}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── STEP: Importing ─── */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12 gap-6">
              <Loader2 className="w-12 h-12 text-[#16c47f] animate-spin" />
              <div className="text-center">
                <p className="text-gray-900 dark:text-white font-semibold text-lg">
                  {language === 'fr' ? 'Import en cours...' : 'Importing...'}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                  {validCount} {language === 'fr' ? 'transactions' : 'transactions'}
                </p>
              </div>
              <div className="w-64 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#16c47f] rounded-full transition-all duration-300"
                  style={{ width: `${importProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* ─── STEP: Done ─── */}
          {step === 'done' && importResult && (
            <div className="flex flex-col items-center justify-center py-12 gap-6">
              {importResult.errors.length === 0 ? (
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-500" />
                </div>
              )}
              <div className="text-center">
                <p className="text-gray-900 dark:text-white font-semibold text-lg">
                  {importResult.imported}{' '}
                  {language === 'fr' ? 'transaction(s) importée(s)' : 'transaction(s) imported'}
                </p>
                {importResult.errors.length > 0 && (
                  <div className="mt-3 max-w-sm">
                    {importResult.errors.slice(0, 3).map((err, i) => (
                      <p key={i} className="text-sm text-red-500 dark:text-red-400">
                        {err}
                      </p>
                    ))}
                    {importResult.errors.length > 3 && (
                      <p className="text-sm text-gray-400">
                        +{importResult.errors.length - 3} more
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50">
          {step === 'map' && (
            <>
              <button
                onClick={() => setStep('upload')}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition"
              >
                <ArrowLeft className="w-4 h-4" />
                {language === 'fr' ? 'Retour' : 'Back'}
              </button>
              <button
                onClick={() => setStep('preview')}
                disabled={!Object.values(columnMap).includes('amount')}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {language === 'fr' ? 'Suivant' : 'Next'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={() => setStep('map')}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition"
              >
                <ArrowLeft className="w-4 h-4" />
                {language === 'fr' ? 'Retour' : 'Back'}
              </button>
              <button
                onClick={handleImport}
                disabled={validCount === 0}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {language === 'fr'
                  ? `Importer ${validCount} transaction(s)`
                  : `Import ${validCount} transaction(s)`}
              </button>
            </>
          )}

          {step === 'done' && (
            <button
              onClick={handleClose}
              className="ml-auto px-5 py-2.5 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl transition-all"
            >
              {language === 'fr' ? 'Fermer' : 'Close'}
            </button>
          )}

          {step === 'upload' && <div />}
        </div>
      </div>
    </div>
  );
}
